import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { hasCapability } from '@/lib/roles';
import { Badge } from '@/components/ui/badge';
import { Bell, ChevronRight, Clock } from 'lucide-react';
import RequestDrawer from '@/components/intake/RequestDrawer';

const urgencyColors = { P0: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', P1: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', P2: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };

// "Waiting for me": the pending approvals assigned to the caller (admin sees
// all; GET /approvals/ is scoped by the backend). A row opens the request in
// the same Drawer as the Intake requests register; decisions happen only
// there, for every gate. ?item=<item id> opens the Drawer directly (old
// /approvals/:itemId links redirect here).
//
// The Drawer reads /intake-review/requests/{id}, which needs intake:read_all.
// A viewer listed as an approver sees the row but cannot open or decide it:
// POST /approvals/{id}/action needs pm.
export default function Approvals() {
  const { user } = useAuth();
  const canOpen = hasCapability(user, 'readAllIntake');
  const [searchParams, setSearchParams] = useSearchParams();
  const openItem = canOpen ? searchParams.get('item') : null;
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchApprovals = useCallback(async () => {
    try {
      const res = await api.get('/approvals/', { params: { status: 'pending' } });
      setApprovals(res.data || []);
      setError('');
    } catch {
      setError('Could not load approvals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApprovals(); }, [fetchApprovals]);

  const open = (itemId) => setSearchParams({ item: itemId });
  const close = () => setSearchParams({});

  return (
    <div className="p-4 lg:p-8" data-testid="approvals-page">
      <div className="mb-10">
        <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Review</p>
        <h1 className="text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>Approvals</h1>
        <p className="text-[#30302F] dark:text-white mt-2 font-light text-lg" data-testid="approvals-summary">
          {approvals.length} {approvals.length === 1 ? 'request is' : 'requests are'} waiting for your decision
        </p>
        {!canOpen && (
          <p className="text-sm text-[#30302F]/70 dark:text-white/70 mt-1" data-testid="approvals-viewer-note">
            Viewers cannot decide approvals.
          </p>
        )}
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-900 dark:text-red-200" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-[#30302F] dark:text-white">Loading...</div>
      ) : approvals.length === 0 ? (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 py-16 text-center">
          <Bell size={40} className="mx-auto mb-3 text-[#30302F] dark:text-white" />
          <p className="text-[#30302F] dark:text-white text-lg font-heading">Nothing is waiting for you</p>
          <p className="text-[#30302F] dark:text-white text-sm mt-1 font-light">All caught up!</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="approvals-list">
          {approvals.map((a) => {
            const body = (
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-[#30302F] dark:text-white">
                    {a.item_ref && <span className="mr-2 text-xs font-mono" data-testid={`approval-item-ref-${a.id}`}>{a.item_ref}</span>}
                    {a.title}
                  </h3>
                  <p className="text-sm text-[#30302F] dark:text-white mt-1 line-clamp-1 font-light">{a.description}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-[#30302F] dark:text-white">
                    <Clock size={12} /> {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {a.urgency && <Badge className={`text-[11px] rounded-none ${urgencyColors[a.urgency] || ''}`}>{a.urgency}</Badge>}
                  <Badge variant="outline" className="text-[11px] rounded-none bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] border-0">{a.gate}</Badge>
                  {canOpen && <ChevronRight size={16} className="text-[#30302F] dark:text-white" aria-hidden="true" />}
                </div>
              </div>
            );
            const card = 'w-full text-left p-5 bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 border-l-4 border-l-[#98139C] transition-all';
            return canOpen && a.item_id ? (
              <button
                key={a.id}
                type="button"
                onClick={() => open(a.item_id)}
                className={`${card} hover:border-l-[#4F0077] cursor-pointer`}
                data-testid={`approval-card-${a.id}`}
              >
                {body}
              </button>
            ) : (
              <div key={a.id} className={card} data-testid={`approval-card-${a.id}`}>{body}</div>
            );
          })}
        </div>
      )}

      <RequestDrawer itemId={openItem} scope="all" onClose={close} onDecided={fetchApprovals} />
    </div>
  );
}
